<script setup lang="ts">
import { BrowserMultiFormatReader } from "@zxing/browser";
import { BarcodeFormat, DecodeHintType } from "@zxing/library";
import type { IScannerControls } from "@zxing/browser";
import type { Result } from "@zxing/library";

/**
 * Generic, reusable camera barcode/QR scanner dialog.
 *
 * Opens the device camera (rear by default), continuously decodes, and emits
 * the first successful read via `decoded`. Works on any modern phone/tablet —
 * Android or iOS — using a JS decoder (`@zxing/browser`) because iOS Safari has
 * no native `BarcodeDetector`.
 *
 * Requires a SECURE CONTEXT (HTTPS, localhost, or Electron `file://`) —
 * `getUserMedia` is blocked on plain HTTP. When the camera is unavailable the
 * dialog degrades gracefully to a clear message instead of throwing, so callers
 * can keep a manual-entry fallback.
 *
 * Reuse it anywhere a scan-to-search is needed by binding `v-model` and
 * listening to `@decoded`. Configure formats/camera via props.
 */
type ScanErrorKind = "insecure-context" | "unsupported" | "permission-denied" | "no-camera" | "start-failed";

const props = withDefaults(
  defineProps<{
    /** Dialog open state (`v-model`). */
    modelValue: boolean;
    /** Dialog title / helper text; fall back to i18n defaults. */
    title?: string;
    hint?: string;
    /** Symbologies to accept. Defaults to the common 1D set + QR (labels use CODE128). */
    formats?: BarcodeFormat[];
    /** Preferred camera. `environment` = rear (default), `user` = front. */
    preferredFacingMode?: "environment" | "user";
    /** Close the dialog automatically on the first successful decode. */
    closeOnScan?: boolean;
    /** Play a short beep on a successful decode. */
    beepOnScan?: boolean;
    /** Max dialog width. */
    maxWidth?: number | string;
    /** Overlay target; only App-level callers pass it (see utils/overlayTarget). */
    attach?: HTMLElement;
  }>(),
  {
    title: undefined,
    hint: undefined,
    formats: () => [
      BarcodeFormat.CODE_128,
      BarcodeFormat.CODE_39,
      BarcodeFormat.EAN_13,
      BarcodeFormat.EAN_8,
      BarcodeFormat.UPC_A,
      BarcodeFormat.UPC_E,
      BarcodeFormat.ITF,
      BarcodeFormat.CODABAR,
      BarcodeFormat.QR_CODE,
      BarcodeFormat.DATA_MATRIX,
    ],
    preferredFacingMode: "environment",
    closeOnScan: true,
    beepOnScan: true,
    maxWidth: 460,
    attach: undefined,
  },
);

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
  (e: "decoded", text: string, result: Result): void;
  (e: "error", kind: ScanErrorKind, err?: unknown): void;
}>();

const { t, locale } = useI18n({ useScope: "global" });

const videoRef = ref<HTMLVideoElement | null>(null);
const starting = ref(false);
const active = ref(false);
const errorKind = ref<ScanErrorKind | null>(null);

// Camera enumeration for the switch button.
const cameras = ref<MediaDeviceInfo[]>([]);
const currentDeviceId = ref<string | null>(null);

let reader: BrowserMultiFormatReader | null = null;
let controls: IScannerControls | null = null;
// Set once a scan has fired so late callback frames don't emit twice.
let handled = false;

const isRtl = computed(() => locale.value?.startsWith("ar"));
const dialogTitle = computed(() => props.title ?? t("alkokh.barcodeScanner.title"));
const dialogHint = computed(() => props.hint ?? t("alkokh.barcodeScanner.hint"));
const canSwitchCamera = computed(() => cameras.value.length > 1 && active.value);

const errorMessage = computed(() => {
  switch (errorKind.value) {
    case "insecure-context":
      return t("alkokh.barcodeScanner.errors.needsHttps");
    case "unsupported":
      return t("alkokh.barcodeScanner.errors.unsupported");
    case "permission-denied":
      return t("alkokh.barcodeScanner.errors.permissionDenied");
    case "no-camera":
      return t("alkokh.barcodeScanner.errors.noCamera");
    case "start-failed":
      return t("alkokh.barcodeScanner.errors.startFailed");
    default:
      return "";
  }
});

const dialogModel = computed<boolean>({
  get: () => props.modelValue,
  set: value => emit("update:modelValue", value),
});

function buildHints() {
  const hints = new Map<DecodeHintType, unknown>();
  hints.set(DecodeHintType.POSSIBLE_FORMATS, props.formats);

  return hints as Map<DecodeHintType, any>;
}

function cameraSupported() {
  return Boolean(
    typeof navigator !== "undefined"
    && navigator.mediaDevices
    && typeof navigator.mediaDevices.getUserMedia === "function",
  );
}

function beep() {
  if (!props.beepOnScan)
    return;

  try {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtx)
      return;

    const ctx = new AudioCtx();
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.type = "square";
    oscillator.frequency.value = 880;
    gain.gain.value = 0.05;
    oscillator.connect(gain);
    gain.connect(ctx.destination);
    oscillator.start();
    oscillator.stop(ctx.currentTime + 0.12);
    oscillator.onended = () => ctx.close().catch(() => {});
  }
  catch {
    // Audio is a nicety; never let it break scanning.
  }
}

/** Stop the decode loop and release the camera on every exit path. */
function stopScanner() {
  active.value = false;

  try {
    controls?.stop();
  }
  catch {
    // ignore
  }
  controls = null;

  // Belt-and-suspenders: stop any tracks still attached to the video element.
  const stream = videoRef.value?.srcObject as MediaStream | null;
  if (stream) {
    for (const track of stream.getTracks())
      track.stop();
    if (videoRef.value)
      videoRef.value.srcObject = null;
  }

  reader = null;
}

function failWith(kind: ScanErrorKind, err?: unknown) {
  errorKind.value = kind;
  starting.value = false;
  stopScanner();
  emit("error", kind, err);
}

function onDecode(result: Result | undefined, _error: unknown, scanControls: IScannerControls) {
  // `_error` is a NotFoundException on every frame without a barcode — that is
  // the normal continuous-scan signal, not a failure, so it is ignored here.
  if (!result || handled)
    return;

  const text = result.getText()?.trim();
  if (!text)
    return;

  handled = true;
  beep();
  emit("decoded", text, result);

  try {
    scanControls.stop();
  }
  catch {
    // ignore
  }

  if (props.closeOnScan)
    dialogModel.value = false;
  else
    stopScanner();
}

async function startScanner(deviceId?: string | null) {
  errorKind.value = null;
  handled = false;

  if (!window.isSecureContext) {
    failWith("insecure-context");

    return;
  }

  if (!cameraSupported()) {
    failWith("unsupported");

    return;
  }

  starting.value = true;

  try {
    reader = new BrowserMultiFormatReader(buildHints());

    await nextTick();
    const video = videoRef.value;
    if (!video) {
      failWith("start-failed");

      return;
    }

    // Prefer an explicit device (camera switch) else the preferred facing mode.
    const constraints: MediaStreamConstraints = deviceId
      ? { video: { deviceId: { exact: deviceId } } }
      : { video: { facingMode: { ideal: props.preferredFacingMode } } };

    controls = await reader.decodeFromConstraints(constraints, video, onDecode);
    active.value = true;
    starting.value = false;

    // Track which device is live and populate the switch list (labels are only
    // available after permission is granted).
    currentDeviceId.value
      = deviceId
      ?? ((video.srcObject as MediaStream | null)?.getVideoTracks?.()[0]?.getSettings?.().deviceId ?? null);
    await refreshCameras();
  }
  catch (err) {
    starting.value = false;
    const name = (err as { name?: string })?.name;
    if (name === "NotAllowedError" || name === "SecurityError")
      failWith("permission-denied", err);
    else if (name === "NotFoundError" || name === "OverconstrainedError")
      failWith("no-camera", err);
    else
      failWith("start-failed", err);
  }
}

async function refreshCameras() {
  try {
    cameras.value = await BrowserMultiFormatReader.listVideoInputDevices();
  }
  catch {
    cameras.value = [];
  }
}

async function switchCamera() {
  if (cameras.value.length < 2)
    return;

  const index = cameras.value.findIndex(cam => cam.deviceId === currentDeviceId.value);
  const next = cameras.value[(index + 1) % cameras.value.length];
  if (!next)
    return;

  stopScanner();
  await startScanner(next.deviceId);
}

function close() {
  dialogModel.value = false;
}

function retry() {
  void startScanner(currentDeviceId.value);
}

// Start when the dialog opens, tear down fully when it closes.
watch(
  () => props.modelValue,
  (open) => {
    if (open)
      void startScanner();
    else
      stopScanner();
  },
);

onBeforeUnmount(stopScanner);
</script>

<template>
  <VDialog
    v-model="dialogModel"
    :max-width="maxWidth"
    :attach="attach"
    :dir="isRtl ? 'rtl' : 'ltr'"
  >
    <VCard class="barcode-scanner">
      <VCardItem>
        <VCardTitle class="d-flex align-center gap-2">
          <VIcon icon="tabler-scan" size="20" />
          {{ dialogTitle }}
        </VCardTitle>
      </VCardItem>

      <VCardText>
        <p class="text-body-2 text-medium-emphasis mb-3">
          {{ dialogHint }}
        </p>

        <div class="barcode-scanner__stage">
          <video
            ref="videoRef"
            class="barcode-scanner__video"
            muted
            autoplay
            playsinline
          />

          <!-- Scan frame overlay -->
          <div
            v-if="active"
            class="barcode-scanner__frame"
          />

          <!-- Starting spinner -->
          <div
            v-if="starting"
            class="barcode-scanner__overlay"
          >
            <VProgressCircular
              indeterminate
              color="primary"
            />
          </div>

          <!-- Graceful unavailable / error state -->
          <div
            v-else-if="errorKind"
            class="barcode-scanner__overlay barcode-scanner__overlay--error"
          >
            <VIcon
              icon="tabler-camera-off"
              size="36"
              class="mb-2"
            />
            <div class="text-body-2 px-4 text-center">
              {{ errorMessage }}
            </div>
          </div>
        </div>
      </VCardText>

      <VCardActions class="px-4 pb-4">
        <VBtn
          v-if="canSwitchCamera"
          variant="text"
          prepend-icon="tabler-camera-rotate"
          @click="switchCamera"
        >
          {{ t("alkokh.barcodeScanner.switchCamera") }}
        </VBtn>
        <VBtn
          v-if="errorKind"
          variant="text"
          prepend-icon="tabler-refresh"
          @click="retry"
        >
          {{ t("alkokh.barcodeScanner.retry") }}
        </VBtn>
        <VSpacer />
        <VBtn
          variant="tonal"
          @click="close"
        >
          {{ t("alkokh.barcodeScanner.cancel") }}
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>

<style scoped>
.barcode-scanner__stage {
  position: relative;
  inline-size: 100%;
  aspect-ratio: 4 / 3;
  overflow: hidden;
  border-radius: 10px;
  background: #000;
}

.barcode-scanner__video {
  inline-size: 100%;
  block-size: 100%;
  object-fit: cover;
  display: block;
}

.barcode-scanner__frame {
  position: absolute;
  inset: 18% 12%;
  border: 2px solid rgba(255, 255, 255, 0.9);
  border-radius: 10px;
  box-shadow: 0 0 0 100vmax rgba(0, 0, 0, 0.25);
  pointer-events: none;
}

.barcode-scanner__overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: rgba(0, 0, 0, 0.55);
}

.barcode-scanner__overlay--error {
  background: rgba(0, 0, 0, 0.72);
}
</style>
